import React, { useState, useRef } from "react";

interface CustomSelectProps {
  label: string;
  name: string;
  value: string | string[];
  onChange: (selectedValues: string | string[]) => void;
  options: { label: string; value: string }[];
  error?: string;
  required?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  isVersioned?: boolean;
  versionedValue?: string | string[];
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  error,
  required = false,
  isMulti = false,
  isSearchable = false,
  disabled = false,
  isVersioned = false,
  versionedValue = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = isSearchable
    ? options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  const handleSelectChange = (selectedValue: string) => {
    if (disabled) return;
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleMultiSelectChange = (selectedValue: string) => {
    if (disabled) return;
    const currentValues = Array.isArray(value) ? [...value] : [];
    const newValues = currentValues.includes(selectedValue)
      ? currentValues.filter((v) => v !== selectedValue)
      : [...currentValues, selectedValue];
    onChange(newValues);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full relative" ref={dropdownRef}>
      <label className="block font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {isVersioned && versionedValue && (
        <div className="relative mb-2">
          <div
            className={`form-select w-full border rounded-lg p-2 bg-white ${error ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-blue-500 ${true ? "cursor-not-allowed opacity-80" : "cursor-pointer"
              }`}
            onClick={() => { }}
          >
            {isMulti ?
              versionedValue :
              options.find((opt) => opt.value === versionedValue)?.label ||
              `Select ${label}`}
          </div>
        </div>
      )}

      {!isMulti ? (
        <div className="relative">
          <div
            className={`form-select w-full border rounded-lg p-2 bg-white ${error ? "border-red-500" : "border-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-blue-500 ${disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"
              }`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            {options.find((opt) => opt.value === value)?.label ||
              `Select ${label}`}
          </div>

          {isOpen && !disabled && (
            <div className="absolute w-full border rounded-lg bg-white shadow-md mt-1 z-10">
              {isSearchable && (
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-full p-2 border-b border-gray-300 focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={disabled}
                />
              )}
              <div className="max-h-60 overflow-y-auto">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleSelectChange(option.value)}
                    >
                      {option.label}
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-gray-500">No results found</div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`form-select w-full border rounded-lg p-2 bg-white ${error ? "border-red-500" : "border-gray-300"
            } focus:outline-none focus:ring-2 focus:ring-blue-500 ${disabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"
            }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          {Array.isArray(value) && value.length > 0
            ? options
              .filter((option) => value.includes(option.value))
              .map((option) => option.label)
              .join(", ")
            : "Select " + label}
        </div>
      )}

      {isMulti && isOpen && !disabled && (
        <div className="absolute w-full border rounded-lg bg-white shadow-md mt-1 z-10">
          {isSearchable && (
            <input
              type="text"
              placeholder="Search..."
              className="w-full p-2 border-b border-gray-300 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={disabled}
            />
          )}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={
                      Array.isArray(value) && value.includes(option.value)
                    }
                    onChange={() => handleMultiSelectChange(option.value)}
                    disabled={disabled}
                  />
                  {option.label}
                </label>
              ))
            ) : (
              <div className="p-2 text-gray-500">No results found</div>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default CustomSelect;
